import React, { useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';

import api from 'api';
import { useRequest, useFeedback, useTableState } from 'shared/hooks';
import useGoBack from 'navigation/hooks/useGoBack';

import PageTitle from 'shared/components/layout/PageTitle/PageTitle';

import { MAX_PAGE_SIZE } from 'utils/constants';

import AddButton from 'navigation/components/AddButton';
import DeleteModal from 'shared/components/feedback/DeleteModal/DeleteModal';
import { Box, FlexBoxSpaceBetweenCenter, FlexBoxColumn } from 'styles/layout';

import ClinicalDecisionSupportAdd from './ClinicalDecisionSupportAdd';
import ClinicalDecisionSupportDetails from './ClinicalDecisionSupportDetails';
import SortableList from './components/SortableList';

const ClinicalDecisionSupportList = () => {
  const { doRequest, data: listData } = useRequest(api.getUserSupportData);
  const navigate = useNavigate();
  const { goBack } = useGoBack();
  const { successDisplay, errorDisplay } = useFeedback();
  const { page } = useTableState();

  const [toDelete, setToDelete] = React.useState(null);

  const listItems = useMemo(() => listData?.data || [], [listData]);

  const fetchList = useCallback(() => {
    doRequest({
      page,
      pageSize: MAX_PAGE_SIZE,
      sortOrder: 'asc',
      sortBy: 'OrderNumber',
    });
  }, [doRequest, page]);

  useEffect(fetchList, [fetchList]);

  const handleAdd = useCallback(
    (values) => {
      api
        .addUserSupportResource(values)
        .then(() => {
          successDisplay('Resource added with success');
          goBack();
          fetchList();
        })
        .catch(({ response }) => {
          errorDisplay(`Error: ${response.data?.title}`);
        });
    },
    [successDisplay, errorDisplay, fetchList, goBack],
  );

  const handleUpdate = useCallback(
    (resourceId, values) => {
      api
        .updateUserSupportResource(resourceId, values)
        .then(() => {
          successDisplay('Resource updated with success');
          goBack();
          fetchList();
        })
        .catch(({ response }) => {
          errorDisplay(`Error: ${response.data?.title}`);
        });
    },
    [successDisplay, errorDisplay, fetchList, goBack],
  );

  const handleBulkReorder = useCallback(
    async (positions) => {
      await api.reorderUserSupportResource({ supportResources: positions });
      fetchList();
    },
    [fetchList],
  );

  return (
    <>
      {toDelete && (
        <DeleteModal
          title="Are you sure you want to delete this resource?"
          onConfirm={() =>
            api.deleteUserSupportResource(toDelete).then(() => {
              setToDelete(null);
              fetchList();
            })
          }
          onCancel={() => setToDelete(null)}
        />
      )}
      <FlexBoxColumn>
        <Routes>
          <Route
            path="/add"
            element={
              <ClinicalDecisionSupportAdd
                onSubmit={handleAdd}
                onCancel={goBack}
              />
            }
          />
          <Route
            path="/:resourceId"
            element={
              <ClinicalDecisionSupportDetails
                onSubmit={handleUpdate}
                onCancel={goBack}
              />
            }
          />
        </Routes>
        <FlexBoxSpaceBetweenCenter>
          <PageTitle>Clinical Decision Support</PageTitle>
          <Box>
            <AddButton to="add">Add Resource</AddButton>
          </Box>
        </FlexBoxSpaceBetweenCenter>

        <SortableList
          items={listItems}
          page={page}
          pageSize={MAX_PAGE_SIZE}
          onRowClick={(id) => navigate(`${id}`)}
          onBulkReorder={handleBulkReorder}
          onDelete={(id) => setToDelete(id)}
        />
      </FlexBoxColumn>
    </>
  );
};

export default ClinicalDecisionSupportList;
